import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { RuleExceptionFilter } from "../types";
import { Search, X, Filter } from "lucide-react";

interface RuleExceptionFiltersProps {
  filters: RuleExceptionFilter;
  onFilterChange: (filters: Partial<RuleExceptionFilter>) => void;
}

export const ExceptionFilters: React.FC<RuleExceptionFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  const clearFilters = () => {
    onFilterChange({
      status: undefined,
      assignedTo: undefined,
      search: undefined,
    });
  };

  const hasActiveFilters = filters.status || filters.assignedTo || filters.search;

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Filter Exceptions</h3>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-slate-500 hover:text-slate-700 h-auto p-1"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Search */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search descriptions or rule keys..."
              value={filters.search || ""}
              onChange={(e) => onFilterChange({ search: e.target.value || undefined })}
              className="pl-9 border-slate-200 focus:border-slate-400 focus:ring-slate-400 h-9"
            />
          </div>
        </div>

        <Separator />

        {/* Status */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">Status</Label>
          <Input
            placeholder="Filter by status (e.g., open, resolved)..."
            value={filters.status || ""}
            onChange={(e) => onFilterChange({ status: e.target.value || undefined })}
            className="border-slate-200 focus:border-slate-400 focus:ring-slate-400 h-9"
          />
        </div>

        {/* Assigned To */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">Assigned To</Label>
          <Input
            placeholder="Filter by assignee email..."
            value={filters.assignedTo || ""}
            onChange={(e) => onFilterChange({ assignedTo: e.target.value || undefined })}
            className="border-slate-200 focus:border-slate-400 focus:ring-slate-400 h-9"
          />
        </div>

        {/* Actions */}
        {hasActiveFilters && (
          <>
            <Separator />
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="w-full text-slate-600 border-slate-200 hover:bg-slate-50 h-9"
            >
              <X className="h-4 w-4 mr-2" />
              Clear All Filters
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
